// components/navigation/HomeCountrySelect.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { HeartFill, ChevronRight } from 'react-bootstrap-icons';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { useTranslation } from 'react-i18next';
import useFeedData from '../../hooks/useFeedData';
import { recentsTableFor } from '../../utils/recentsRouting';
import { canadaFeedsConfig } from '../../utils/canadaFeedsConfig';
import { mexicoFeedsConfig } from '../../utils/mexicoFeedsConfig';
import { unitedKingdomFeedsConfig } from '../../utils/unitedKingdomFeedsConfig';
import FeedItem from '../feed/FeedItem';
import SearchNavBar from '../search/SearchNavBar';
import BottomNavbar from '../navigation/Menu';
import DeskCarousel from '../carousel/DeskCarousel';
import './Home.css';

// Country configuration
// Country names left as English data — consistent with how country names appear
// elsewhere in the app (search results, feed attributions, state pages, etc.).
const COUNTRIES = {
  'us': { name: 'United States', flag: '🇺🇸', schema: 'united_states_of_america', enabled: true },
  'canada': { name: 'Canada', flag: '🇨🇦', schema: 'canada', enabled: true },
  'mexico': { name: 'Mexico', flag: '🇲🇽', schema: 'mexico', enabled: true },
  'uk': { name: 'United Kingdom', flag: '🇬🇧', schema: 'united_kingdom', enabled: true },
  'australia': { name: 'Australia', flag: '🇦🇺', schema: 'australia', enabled: true },
  'france': { name: 'France', flag: '🇫🇷', schema: 'france', enabled: true },
  'italy': { name: 'Italy', flag: '🇮🇹', schema: 'italy', enabled: true },
  'japan': { name: 'Japan', flag: '🇯🇵', schema: 'japan', enabled: true },
  'south-africa': { name: 'South Africa', flag: '🇿🇦', schema: 'south_africa', enabled: true },
  'ukraine': { name: 'Ukraine', flag: '🇺🇦', schema: 'ukraine', enabled: true },
};

// Recents feed configurations as array for ordering control.
//
// Canada, Mexico and the UK take their endpoint straight from their own feeds
// config, so this page shows the same Recents feed their country page does —
// the schema-level /posts/recent/schema call rather than a country-filtered
// slice of public/news_items. Sourcing the endpoint rather than copying it
// keeps the two in step if a config changes.
//
// `itemSchema` is the schema those items belong to, used for routing: a
// schema-level endpoint carries no schema/table pair that resolves to a real
// feed page, so clicks need to be sent to /feed/{itemSchema}/{item table}.
// Countries still served by public/news_items have no per-schema tables behind
// them, so they leave it unset and keep the existing behaviour.
const RECENTS_CONFIG_BASE = [
  {
    key: 'us',
    apiEndpoint: '&schema=united_states_of_america&table=all_recents&order_by=created_at&skip=0',
    viewAllLink: '/us',
    feedName: 'US Recents',
    itemSchema: 'united_states_of_america'
  },
  {
    key: 'canada',
    apiEndpoint: canadaFeedsConfig.recent[0].apiEndpoint,
    viewAllLink: '/canada',
    feedName: 'Canada Recents',
    itemSchema: 'canada'
  },
  {
    key: 'mexico',
    apiEndpoint: mexicoFeedsConfig.recent[0].apiEndpoint,
    viewAllLink: '/mexico',
    feedName: 'Mexico Recents',
    itemSchema: 'mexico'
  },
  {
    key: 'uk',
    apiEndpoint: unitedKingdomFeedsConfig.recent[0].apiEndpoint,
    viewAllLink: '/uk',
    feedName: 'UK Recents',
    itemSchema: 'united_kingdom'
  },
  {
    key: 'australia',
    apiEndpoint: '&skip=0&schema=public&table=news_items&order_by=created_at&filter_column=country&filter_string=Australia',
    viewAllLink: '/australia',
    feedName: 'Australia Recents'
  },
  {
    key: 'france',
    apiEndpoint: '&skip=0&schema=public&table=news_items&order_by=created_at&filter_column=country&filter_string=France',
    viewAllLink: '/france',
    feedName: 'France Recents'
  },
  {
    key: 'italy',
    apiEndpoint: '&skip=0&schema=public&table=news_items&order_by=created_at&filter_column=country&filter_string=Italy',
    viewAllLink: '/italy',
    feedName: 'Italy Recents'
  },
  {
    key: 'japan',
    apiEndpoint: '&skip=0&schema=public&table=news_items&order_by=created_at&filter_column=country&filter_string=Japan',
    viewAllLink: '/japan',
    feedName: 'Japan Recents'
  },
  {
    key: 'south-africa',
    apiEndpoint: '&skip=0&schema=public&table=news_items&order_by=created_at&filter_column=country&filter_string=South Africa',
    viewAllLink: '/south-africa',
    feedName: 'South Africa Recents'
  },
  {
    key: 'ukraine',
    apiEndpoint: '&skip=0&schema=public&table=news_items&order_by=created_at&filter_column=country&filter_string=Ukraine',
    viewAllLink: '/ukraine',
    feedName: 'Ukraine Recents'
  }
];

// Map homeCountry codes to RECENTS_CONFIG keys
const HOME_COUNTRY_TO_KEY = {
  'us': 'us',
  'ca': 'canada',
  'mx': 'mexico',
  'uk': 'uk',
  'australia': 'australia',
  'france': 'france',
  'italy': 'italy',
  'japan': 'japan',
  'south-africa': 'south-africa',
  'ukraine': 'ukraine'
};

const ITEMS_PER_COUNTRY = 5;

// Haptics helper
const handleHapticsClick = async (callback) => {
  try {
    await Haptics.impact({ style: ImpactStyle.Light });
  } catch (e) {
    // Haptics not available (web)
  }
  callback();
  window.scrollTo(0, 0);
};

// Home country first, everything else in declared order
const useSortedCountryConfigs = () => useMemo(() => {
  const homeCountry = localStorage.getItem('homeCountry');
  const homeKey = homeCountry ? HOME_COUNTRY_TO_KEY[homeCountry] : null;

  if (!homeKey) {
    return RECENTS_CONFIG_BASE;
  }

  const homeConfig = RECENTS_CONFIG_BASE.find(c => c.key === homeKey);
  const otherConfigs = RECENTS_CONFIG_BASE.filter(c => c.key !== homeKey);

  return homeConfig ? [homeConfig, ...otherConfigs] : RECENTS_CONFIG_BASE;
}, []);

const countryLabel = (countryKey) => {
  const country = COUNTRIES[countryKey];
  return country ? `${country.flag} ${country.name}` : countryKey;
};

// ═══════════════════════════════════════════════════════════════════
// MOBILE — one horizontal scroll carousel per country, stacked
// ═══════════════════════════════════════════════════════════════════

const MobileCountryCarousel = ({ countryKey, config, isFirstCarousel = false }) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { data, loading, error } = useFeedData(config.apiEndpoint, ITEMS_PER_COUNTRY);
  const country = COUNTRIES[countryKey];

  const urlParams = new URLSearchParams(config.apiEndpoint.replace(/^[&?]/, ''));
  // itemSchema wins when set: a schema-level recents endpoint has no table, and
  // the public/news_items ones parse to schema=public, which is the aggregation
  // table rather than anywhere a card should open.
  const schema = config.itemSchema || urlParams.get('schema') || '';
  const table = urlParams.get('table') || '';

  const openCountry = () => handleHapticsClick(() => navigate(config.viewAllLink));

  return (
    <div className={`homepage-branch-carousel country-carousel ${isFirstCarousel ? 'homepage-first-carousel' : ''}`}>
      <div className="branch-header">
        <div
          className="branch-title"
          onClick={openCountry}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              openCountry();
            }
          }}
        >
          <span className="country-flag">{country?.flag}</span>
          <span className="country-name">{country?.name}</span>
          <ChevronRight className="branch-chevron" />
        </div>
      </div>

      <div className="carouselGroupContainer news-section">
        <div className="scroll-container">
          <div className="gradient-left" />
          <div className="gradient-right" />
          <div className="scroll-area">
            <div className="card-container">
              {loading && (
                <div className="card-wrapper">
                  <div className="mobile-carousel-loading">
                    <Spinner animation="grow" variant="light" />
                  </div>
                </div>
              )}

              {!loading && (error || !data || data.length === 0) && (
                <div className="card-wrapper">
                  <div className="mobile-carousel-empty">
                    <p>
                      {error
                        ? t('common:errorLoading', 'Error loading')
                        : t('common:noContent', 'No content')}
                    </p>
                  </div>
                </div>
              )}

              {!loading && data && data.map((item, idx) => {
                const displayFeedName = item.collection_name || item.source || config.feedName;
                const displayTable = recentsTableFor(item, table);
                return (
                  <div key={item.id || idx} className="card-wrapper">
                    <div className="card-content">
                      <div className="courtNewsMobile">
                        <div className="country-card-inner">
                          <FeedItem
                            item={item}
                            feedName={displayFeedName}
                            branch="N/A"
                            topic="N/A"
                            buttonName="Document"
                            schema={schema}
                            table={displayTable}
                            expanded={false}
                            isRecentsItem={true}
                            variant="homepage"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

              <div className="card-wrapper view-all-card">
                <div id="viewFullPageButton">
                  <Button
                    id="viewFullLegislativePageButtonMobile"
                    size="lg"
                    onClick={openCountry}
                  >
                    {t('common:viewAllNews', 'View all {{name}} news', { name: country?.name })}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// DESKTOP — one DeskCarousel per country, stacked
// ═══════════════════════════════════════════════════════════════════

// Build the rotating sections for a single country: the same endpoint with an
// incrementing skip, so the carousel cycles through the latest N items.
//
// isRecentsItem / recentsSchema are the props FeedSection uses to route recents
// cards to the country schema rather than to the aggregation table. They're
// passed through the section objects for DeskCarousel to forward; if it doesn't
// forward them yet, the cards still render and only their links are affected.
const buildCountrySections = (config, itemsPerFeed = ITEMS_PER_COUNTRY) => {
  const sections = [];
  for (let i = 0; i < itemsPerFeed; i++) {
    let endpoint = config.apiEndpoint;
    if (endpoint.includes('skip=')) {
      endpoint = endpoint.replace(/skip=\d+/, `skip=${i}`);
    } else {
      endpoint = `${endpoint}&skip=${i}`;
    }

    sections.push({
      type: 'normal',
      id: `${config.key}-${i}`,
      title: countryLabel(config.key),
      apiEndpoint: endpoint,
      feedName: config.feedName,
      branch: 'N/A',
      topic: 'N/A',
      buttonName: 'Document',
      viewAllLink: config.viewAllLink,
      isRecentsItem: true,
      recentsSchema: config.itemSchema || null
    });
  }
  return sections;
};

const DesktopCountryCarousel = ({ config }) => {
  const sections = useMemo(() => buildCountrySections(config), [config]);

  return (
    <div className="desktop-country-carousel">
      <DeskCarousel
        title={countryLabel(config.key)}
        icon={null}
        sections={sections}
        viewAllLink={config.viewAllLink}
        intervalDuration={30000}
        showFeedTitle={false}
      />
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// MAIN
// ═══════════════════════════════════════════════════════════════════

const HomeCountrySelect = () => {
  const { t } = useTranslation('common');
  const [isMobile, setIsMobile] = useState(false);
  const sortedCountries = useSortedCountryConfigs();

  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent.toLowerCase();
      const isMobileDevice = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/.test(userAgent);
      const isSmallScreen = window.innerWidth <= 768;
      setIsMobile(isMobileDevice || isSmallScreen);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  const openDonate = (e) => {
    e.preventDefault();
    window.open('https://donate.stripe.com/28o3fXbdvbQO2RO288', '_blank');
  };

  return (
    <div id='homebg' className="all-countries-page">

      {!isMobile && <BottomNavbar />}

      <div className={`all-countries-body ${isMobile ? '' : 'all-countries-body-desktop'}`}>
        {sortedCountries.map((config, index) => (
          isMobile
            ? (
              <MobileCountryCarousel
                key={config.key}
                countryKey={config.key}
                config={config}
                isFirstCarousel={index === 0}
              />
            )
            : (
              <DesktopCountryCarousel
                key={config.key}
                config={config}
              />
            )
        ))}

        <div className="all-countries-mission">
          <p>
            {t('common:missionStatement', 'Gov Glance is a completely free tool designed to help citizens get easy access to official government news and information.')}
          </p>
          <p>
            {t('common:missionGoal', 'It is our mission to cover every country, state, and city in the world.')}
          </p>
          <Button size='lg' id='donateButton' onClick={openDonate}>
            <HeartFill id='donateIcon' /> {t('common:supportGovGlance', 'Support Gov Glance')}
          </Button>
        </div>

        {isMobile && <div id='mobileNavbarSpacer'></div>}
      </div>

      <style>{`
        /* ─── Page shell ─── */
        .all-countries-page .all-countries-body { padding-top: 8px; }

        /* ─── Mobile: stacked horizontal carousels ─── */
        .all-countries-page .homepage-branch-carousel { margin-bottom: 12px; }
        .all-countries-page .homepage-first-carousel { margin-top: 0 !important; }

        .all-countries-page .branch-header { padding: 6px 20px 12px; color: var(--text-primary); }
        .all-countries-page .branch-title {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 17px;
          font-weight: bold;
          cursor: pointer;
          color: var(--text-primary);
        }
        .all-countries-page .branch-title .country-name {
          color: var(--text-primary);
          white-space: nowrap;
        }
        .all-countries-page .branch-title .country-flag { font-size: 20px; line-height: 1; }
        .all-countries-page .branch-chevron {
          width: 18px;
          height: 18px;
          margin-left: 2px;
          color: var(--text-primary);
          fill: currentColor;
          flex-shrink: 0;
        }

        .all-countries-page .news-section { margin-bottom: 0; }
        .all-countries-page .scroll-container { position: relative; height: auto !important; }
        .all-countries-page .gradient-left,
        .all-countries-page .gradient-right {
          position: absolute;
          top: 0;
          bottom: 0;
          width: 48px;
          z-index: 5;
          pointer-events: none;
        }
        .all-countries-page .gradient-left { left: 0; }
        .all-countries-page .gradient-right { right: 0; }

        .all-countries-page .scroll-area {
          box-sizing: border-box;
          overflow-x: scroll;
          overflow-y: hidden;
          scroll-snap-type: x mandatory;
          scrollbar-width: none;
          -ms-overflow-style: none;
          height: auto !important;
        }
        .all-countries-page .scroll-area::-webkit-scrollbar { display: none; }

        .all-countries-page .card-container { display: flex; padding: 0 10px 6px; height: auto !important; }
        .all-countries-page .card-wrapper {
          flex: 0 0 min(430px, 90vw) !important;
          padding-right: 1rem;
          scroll-snap-align: center;
          box-sizing: border-box;
        }
        .all-countries-page .card-wrapper:first-child { padding-left: 10px; }
        .all-countries-page .card-content {
          height: 160px;
          display: flex;
          flex-direction: column;
          box-sizing: border-box;
          overflow: hidden;
        }
        .all-countries-page .country-card-inner { height: 160px; min-height: 160px; overflow: hidden; }

        .all-countries-page .mobile-carousel-loading,
        .all-countries-page .mobile-carousel-empty {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 160px;
          border-radius: 8px;
        }
        .all-countries-page .mobile-carousel-empty p {
          color: var(--text-tertiary);
          font-size: 14px;
          text-align: center;
          margin: 0;
        }

        .all-countries-page .view-all-card { display: flex; align-items: center; }

        /* ─── Mission / donate ─── */
        .all-countries-page .all-countries-mission {
          text-align: center;
          color: var(--text-primary);
          margin: 40px auto;
          max-width: 680px;
          padding: 0 20px;
        }
        .all-countries-page .all-countries-mission p {
          font-size: 16px;
          line-height: 1.6;
          margin-bottom: 16px;
        }

        @media screen and (max-width: 350px) {
          .all-countries-page .branch-title { font-size: 15px; }
        }

        /* ═══ Search bar sizing, scoped to this page ═══
           SearchNavBar renders a position: fixed navbar, so wrapping it in a
           narrow container does nothing. It is still a DOM descendant of
           .all-countries-page, so its own elements can be targeted directly —
           and .all-countries-page #id outranks the plain #id rules the navbar
           injects into <head>. The navbar-spacer height is measured by a
           ResizeObserver, so it shrinks to match automatically. */
        @media (min-width: 769px) {
          .all-countries-page #searchContainer {
            max-width: 1080px;
            margin: 0 auto;
            padding-top: 6px !important;
            padding-bottom: 6px !important;
          }
          .all-countries-page .search-row { align-items: center; }
          .all-countries-page #ggSearch2 {
            max-width: 340px;
            height: 34px;
            min-height: 34px;
            font-size: 13px;
            padding: 4px 12px;
          }
          .all-countries-page #searchCountry {
            padding: 2px 8px;
            font-size: 15px;
          }
          .all-countries-page .desktop-nav-btn {
            padding: 5px 11px;
            font-size: 12px;
          }
          .all-countries-page .desktop-nav-btn svg { width: 12px; height: 12px; }
        }

        @media (min-width: 769px) {
          /* ─── Desktop: stacked DeskCarousels in a single centered column ─── */
          .all-countries-page .all-countries-body-desktop {
            max-width: 820px;
            margin: 0 auto;
            padding: 8px 16px 0;
          }
          .all-countries-page .desktop-country-carousel { margin-bottom: 28px; }

          /* Full country name gets the room it needs in the carousel heading */
          .all-countries-page .desktop-country-carousel .carousel-heading {
            padding-left: 0;
            padding-right: 8px;
          }
          .all-countries-page .desktop-country-carousel .heading-text {
            font-size: 20px;
            font-weight: 700;
            max-width: none;
            overflow: visible;
            text-overflow: clip;
          }
          .all-countries-page .desktop-country-carousel .title-container { max-width: none; }

          .all-countries-page .all-countries-mission p { font-size: 18px; }
        }

        [data-theme="dark"] .all-countries-page .branch-title,
        [data-theme="dark"] .all-countries-page .branch-title .country-name,
        [data-theme="dark"] .all-countries-page .all-countries-mission { color: #ffffff; }
        [data-theme="dark"] .all-countries-page .branch-chevron { color: #ffffff; fill: #ffffff; }
        [data-theme="dark"] .all-countries-page .mobile-carousel-empty p { color: rgba(255, 255, 255, 0.65); }

        [data-theme="light"] .all-countries-page .branch-title,
        [data-theme="light"] .all-countries-page .branch-title .country-name,
        [data-theme="light"] .all-countries-page .all-countries-mission { color: #000000 !important; }
        [data-theme="light"] .all-countries-page .branch-chevron { color: #000000 !important; fill: #000000 !important; }
        [data-theme="light"] .all-countries-page .mobile-carousel-empty p { color: #666666; }
      `}</style>
    </div>
  );
};

export default HomeCountrySelect;